import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { cn } from '../../utils/cn';
import {
  LayoutDashboard,
  Users,
  UsersRound,
  Workflow,
  PlayCircle,
  Brain,
  CreditCard,
  FileText,
  HardDrive,
  Key,
  Webhook,
  ShieldAlert,
  FileClock,
  Settings,
  LogOut,
  Menu,
  X,
  Ticket,
} from 'lucide-react';
import { useState } from 'react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  permission?: string;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/users', label: '用户管理', icon: Users, permission: 'users:read' },
  { path: '/teams', label: '团队管理', icon: UsersRound, permission: 'teams:read' },
  { path: '/workflows', label: '工作流管理', icon: Workflow, permission: 'workflows:read' },
  { path: '/executions', label: '运行记录', icon: PlayCircle, permission: 'executions:read' },
  { path: '/ai', label: 'AI 使用管理', icon: Brain, permission: 'ai:read' },
  { path: '/subscriptions', label: '订阅与支付', icon: CreditCard, permission: 'subscriptions:read' },
  { path: '/templates', label: '模板市场', icon: FileText, permission: 'templates:read' },
  { path: '/files', label: '文件存储', icon: HardDrive, permission: 'files:read' },
  { path: '/api-keys', label: 'API Keys', icon: Key, permission: 'apikeys:read' },
  { path: '/webhooks', label: 'Webhook 记录', icon: Webhook },
  { path: '/risk', label: '风控系统', icon: ShieldAlert, permission: 'risk:read' },
  { path: '/logs', label: '系统日志', icon: FileClock, permission: 'logs:read' },
  { path: '/config', label: '系统配置', icon: Settings, permission: 'config:read' },
  { path: '/activation-codes', label: '激活码管理', icon: Ticket },
];

export function MainLayout() {
  const { user, logout, hasPermission } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(
    (item) => !item.permission || hasPermission(item.permission)
  );

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-zinc-200 fixed h-full z-10">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-zinc-100">
          <div className="w-8 h-8 rounded-lg bg-accent-600 flex items-center justify-center mr-3">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <span className="text-lg font-bold text-zinc-900">Admin</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-accent-50 text-accent-700'
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                  )}
                >
                  <Icon className={cn('w-5 h-5', isActive ? 'text-accent-600' : 'text-zinc-400')} />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-zinc-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
              <span className="text-accent-700 font-semibold">
                {user?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {user?.username}
              </p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-zinc-600 hover:text-error-600 hover:bg-error-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            退出登录
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-zinc-200 z-20 flex items-center justify-between px-4">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-lg bg-accent-600 flex items-center justify-center mr-3">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <span className="text-lg font-bold text-zinc-900">Admin</span>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg text-zinc-600 hover:bg-zinc-100"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-10 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <div
            className="absolute top-16 left-0 right-0 bg-white border-b border-zinc-200 max-h-[calc(100vh-4rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="p-4 space-y-1">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'
                    )}
                  >
                    <Icon className={cn('w-5 h-5', isActive ? 'text-accent-600' : 'text-zinc-400')} />
                    {item.label}
                  </NavLink>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-600 hover:text-error-600 hover:bg-error-50 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                退出登录
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
